import {
	BettingOffer,
	Event,
	EventOffer,
	KeyOffer,
	PageableOfferResponse,
	SportCategoryGroup,
	TournamentOffer,
} from "@gp/models";
import {
	EventElement,
	ItemItem,
	TournamentGroupingResultsHistoryResponse,
	Items,
} from "@api-types/offer/offer-history/TournamentGroupingResultsHistoryResponse";
import { buildHistoryEventDefaults } from "./historyEventDefaults";

/**
 * Remaps a JSON object from the new source structure (Results history api endpoint) to the target structure (Offer/data endpoint).
 * @param input The source data object
 * @returns A new object mapped to the target structure.
 */
export function tournamentGroupingResultsHistoryMapper(
	input: TournamentGroupingResultsHistoryResponse
): PageableOfferResponse {
	const remappedData: PageableOfferResponse = {
		version: 0,
		lookup: input.lookup,
		items: sportMapping(input.items, input.lookup),
		pageNumber: input.pageNumber,
		pageSize: input.pageSize,
		totalNumberOfItems: input.totalNumberOfItems,
		startingVersion: 0,
	};
	return remappedData;
}
function sportMapping(
	sports: Items[],
	lookup: TournamentGroupingResultsHistoryResponse["lookup"]
): SportCategoryGroup[] {
	if (!sports?.length) {
		return [];
	}

	return sports.map((sport) => {
		let sportObject: SportCategoryGroup = {
			categoryId: sport.categoryId,
			sportId: sport.sportId,
			otherOffers: sport.otherOffers,
			items: tournamentMapping(sport.items, lookup),
		};
		return sportObject;
	});
}
function tournamentMapping(
	tournaments: ItemItem[],
	lookup: TournamentGroupingResultsHistoryResponse["lookup"]
): TournamentOffer[] {
	if (!tournaments?.length) {
		return [];
	}

	return tournaments.map((tournament) => {
		let sportObject: TournamentOffer = {
			tournamentId: tournament.tournamentId,
			isSpecial: tournament.isSpecial,
			events: eventsMapping(tournament.events, lookup),
		};
		return sportObject;
	});
}
function eventsMapping(
	eventOffers: EventElement[],
	lookup: TournamentGroupingResultsHistoryResponse["lookup"]
): EventOffer[] {
	if (!eventOffers?.length) {
		return [];
	}
	const lookups = lookup;
	return eventOffers.map((event) => {
		const sourceEvent = event.event;

		let sportIdFromLookup: string | null = null;
		if (lookups && lookups.categories && sourceEvent.sportCategoryId) {
			const category = lookups.categories[sourceEvent.sportCategoryId];
			if (category && category.sportId) {
				sportIdFromLookup = category.sportId;
			}
		}

		const mappedEvent: Event = {
			...sourceEvent,
			...buildHistoryEventDefaults(),
			id: sourceEvent.id,
			sportCategoryId: sourceEvent.sportCategoryId,
			startTime: sourceEvent.startTime,
			teamOneId: sourceEvent.teamOneId,
			teamTwoId: sourceEvent.teamTwoId,
			tournamentId: sourceEvent.tournamentId,
			sportId: sportIdFromLookup || "",
			totalOfferCount: sourceEvent.totalOfferCount || 0,
			source: sourceEvent.source as unknown as Event["source"],
		};

		// --- Offer Transformation ---
		const mappedOffers = event.keys?.map((sourceOffer, index) => {
			// const generatedKeyId = `generated-key-${index}-${sourceOffer.key.bettingTypeId}`;

			const mappedOutcomes: BettingOffer[] = sourceOffer.offers.map(
				(sourceOutcome, outcomeIndex) => {
					// const generatedOutcomeId = `generated-outcome-${index}-${outcomeIndex}`;
					return {
						id: sourceOutcome.id,
						specifier: sourceOffer.key?.specifier,
						tip: sourceOutcome.tip,
						status: sourceOutcome.status,
						value: 0,
						playerId: sourceOutcome.playerId,
						teamId: sourceOutcome.teamId,
						betBuilder: false,
						displayTip: "",
					};
				}
			);

			const mappedOffer: KeyOffer = {
				bettingTypeId: sourceOffer.key?.bettingTypeId,
				keyId: sourceOffer.key.id,
				key: {
					bettingTypeId: sourceOffer.key?.bettingTypeId,
					id: sourceOffer.key.id,
					specifier: sourceOffer.key?.specifier,
					betBuilder: false,
				},
				offers: mappedOutcomes,
			};
			return mappedOffer;
		});

		return {
			event: mappedEvent,
			eventId: mappedEvent.id,
			offers: mappedOffers || [],
		};
	});
}
